<template>
  <v-container fluid class="ma-0 pa-0">
    <v-app-bar v-if="showAppBar" app class="primary">
      <v-icon color="white" large>mdi-layers-search</v-icon>
      <v-toolbar-title class="white--text pl-3"
        >Dataset Summary
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-tooltip bottom color="primary">
        <template v-slot:activator="{ on: tooltip }">
          <v-btn color="primary--text" @click="goBack()" v-on="{ ...tooltip }">
            <v-icon left>library_add</v-icon>
            Create Dataset
          </v-btn>
        </template>
        <span>Return to Create Dataset</span>
      </v-tooltip>
    </v-app-bar>

    <v-container v-if="isLoading" class="ma-0 pa-2">
      <v-row class="ma-0 pa-0">
        <v-col cols="12" class="ma-0 pa-0"> <loading-spinner /> </v-col>
      </v-row>
    </v-container>

    <v-container v-else fluid class="ma-0 pa-3">
      <v-row class="ma-0 pa-0">
        <v-col cols="12" class="ma-0 pa-0">
          <v-card class="pa-3 ma-0">
            <v-row class="ma-0 pa-0">
              <span class="title">{{ dataset.study_name }}</span>
            </v-row>

            <v-row class="ma-0 pa-0">
              <span
                class="text-subtitle-1 my-2 text-wrap"
                style="line-height: 1.25em;"
                >{{ dataset.description }}</span
              >
            </v-row>

            <v-divider class="ma-0 pa-0 my-2"></v-divider>

            <v-row class="ma-0 pa-0">
              <span class="text-subtitle-1"
                ><b>Project: </b>{{ dataset.project.project_name }}</span
              >
            </v-row>

            <v-row class="ma-0 pa-0">
              <span
                class="text-subtitle-1 my-2 text-wrap"
                style="line-height: 1.25em;"
                >{{ dataset.project.description }}</span
              >
            </v-row>

            <v-row class="ma-0 pa-0 mt-1">
              <v-col class="ma-0 pa-0">
                <div class="ma-0 pa-0" style="display: flex;" align="center">
                  <v-tooltip
                    v-if="dataset.project.is_public"
                    top
                    color="primary"
                  >
                    <template v-slot:activator="{ on: tooltip }">
                      <span v-on="{ ...tooltip }">
                        <v-chip
                          label
                          class="font-weight-bold pa-2 mr-2"
                          color="green lighten-4"
                        >
                          <v-icon center>mdi-lock-open-variant-outline</v-icon>
                        </v-chip>
                        <span class="black--text text-body-1"> Public </span>
                      </span>
                    </template>
                    <span> Dataset is public </span>
                  </v-tooltip>

                  <v-tooltip v-else top color="primary">
                    <template v-slot:activator="{ on: tooltip }">
                      <span v-on="{ ...tooltip }">
                        <v-chip
                          label
                          class="font-weight-bold pa-2 mr-2"
                          color="red lighten-4"
                        >
                          <v-icon center>mdi-lock-outline</v-icon>
                        </v-chip>
                        <span class="black--text text-body-1"> Private </span>
                      </span>
                    </template>
                    <span class="subtitle-1"> Dataset is private </span>
                  </v-tooltip>

                  <v-tooltip v-if="dataset.longitudinal" top color="primary">
                    <template v-slot:activator="{ on: tooltip }">
                      <span class="ml-4" v-on="{ ...tooltip }">
                        <v-chip
                          label
                          class="font-weight-bold pa-3 mr-2"
                          color="green lighten-4"
                        >
                          L
                        </v-chip>
                        <span class="black--text text-body-1">
                          Longitudinal
                        </span>
                      </span>
                    </template>
                    <span> Dataset is a longitudinal dataset </span>
                  </v-tooltip>

                  <v-tooltip v-else top color="primary">
                    <template v-slot:activator="{ on: tooltip }">
                      <span class="ml-4" v-on="{ ...tooltip }">
                        <v-chip
                          label
                          class="font-weight-bold pa-3 mr-2"
                          color="blue lighten-4"
                        >
                          CS
                        </v-chip>
                        <span class="black--text text-body-1">
                          Cross-sectional
                        </span>
                      </span>
                    </template>
                    <span> Dataset is a cross-sectional dataset </span>
                  </v-tooltip>

                  <v-tooltip bottom color="primary">
                    <template v-slot:activator="{ on: tooltip }">
                      <span class="ml-4" v-on="{ ...tooltip }">
                        <v-chip
                          label
                          color="primary"
                          class="white--text title mr-2"
                        >
                          {{ variables.length }}
                        </v-chip>
                        <span class="black--text text-body-1"> Variables </span>
                      </span>
                    </template>
                    <span>
                      Dataset contains {{ variables.length }} variables
                    </span>
                  </v-tooltip>

                  <v-tooltip bottom color="primary">
                    <template v-slot:activator="{ on: tooltip }">
                      <span class="ml-4" v-on="{ ...tooltip }">
                        <v-chip
                          label
                          color="primary"
                          class="white--text title mr-2"
                        >
                          {{ dataset.num_subjects }}
                        </v-chip>
                        <span class="black--text text-body-1">
                          Population
                        </span>
                      </span>
                    </template>
                    <span>
                      Dataset contains {{ dataset.num_subjects }} subjects in
                      study population
                    </span>
                  </v-tooltip>

                  <v-spacer />

                  <!-- NOTE: Remove `v-if` once correlation summary is implemented. Currently, there is no reason to unselect variables in cross-sectional dataset -->
                  <v-switch
                    v-if="dataset.longitudinal"
                    v-model="enableVariableSelectionSelect"
                    label="Enable variable selection"
                    class="pa-0 ma-0 pr-6"
                    hide-details
                  ></v-switch>

                  <v-switch
                    v-model="useLongScaleNamesSelect"
                    label="Use long variable names"
                    class="pa-0 ma-0 pr-3"
                    hide-details
                  ></v-switch>
                </div>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>

      <v-row class="ma-0 pa-0 mt-4">
        <v-col cols="12" class="ma-0 pa-0">
          <v-card class="ma-0 pa-0">
            <v-tabs v-model="summaryTab" light class="ma-0 pa-0">
              <v-tab key="variables"><span>Variables</span></v-tab>

              <v-tab key="visits" :disabled="!dataset.longitudinal"
                ><span>Visits</span></v-tab
              >
              <v-tab key="dashboard"><span>Data Dashboard</span></v-tab>
              <v-tab key="correlation"><span>Correlation Summary</span></v-tab>
            </v-tabs>

            <v-tabs-items v-model="summaryTab" class="px-3 pb-3 ma-0">
              <v-tab-item key="variables" :eager="true">
                <variable-table />
              </v-tab-item>

              <v-tab-item key="visits" :eager="true">
                <visit-summary />
              </v-tab-item>

              <!-- deliberately not eager: the dashboard computes on mount -->
              <v-tab-item key="dashboard"> <data-dashboard /> </v-tab-item>

              <!-- not eager either: the matrix is computed on mount -->
              <v-tab-item key="correlation">
                <correlation-summary />
              </v-tab-item>
            </v-tabs-items>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-container>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { state, actions } from '@/store/modules/datasetSummary/types';
import LoadingSpinner from '@/components/common/LoadingSpinner.vue';
import VisitSummary from '@/components/DatasetSummary/VisitSummary.vue';
import VariableTable from '@/components/DatasetSummary/VariableTable.vue';
import DataDashboard from '@/components/DatasetSummary/DataDashboard/DataDashboard.vue';
import CorrelationSummary from '@/components/DatasetSummary/Correlations/CorrelationSummary.vue';

export default {
  components: {
    CorrelationSummary,
    DataDashboard,
    LoadingSpinner,
    VariableTable,
    VisitSummary,
  },
  props: {
    id: {
      type: Number,
      required: true,
    },
    showAppBar: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isLoading: true,
      summaryTab: 0,
    };
  },
  computed: {
    ...mapState('datasetSummary', {
      dataset: state.DATASET,
      variables: state.VARIABLES,
      useLongScaleNames: state.USE_LONG_SCALE_NAMES,
      enableVariableSelection: state.ENABLE_VARIABLE_SELECTION,
    }),
    useLongScaleNamesSelect: {
      get() {
        return this.useLongScaleNames;
      },
      set(value) {
        this.setUseLongScaleNames(value);
      },
    },
    enableVariableSelectionSelect: {
      get() {
        return this.enableVariableSelection;
      },
      set(value) {
        this.setEnableVariableSelection(value);
      },
    },
  },
  watch: {
    id() {
      this.initialize();
    },
  },
  created() {
    this.initialize();
  },
  methods: {
    ...mapActions('datasetSummary', {
      fetchDataset: actions.FETCH_DATASET,
      setIsCollection: actions.SET_IS_COLLECTION,
      setUseLongScaleNames: actions.SET_USE_LONG_SCALE_NAMES,
      setEnableVariableSelection: actions.SET_ENABLE_VARIABLE_SELECTION,
      resetAll: actions.RESET_ALL,
    }),
    async initialize() {
      this.isLoading = true;

      this.summaryTab = 0;
      this.useLongScaleNamesSelect = false;
      this.enableVariableSelectionSelect = false;

      await this.resetAll();
      await this.setIsCollection(false);
      await this.fetchDataset(this.id);

      this.isLoading = false;
    },
    goBack() {
      this.$router.push('/datasets');
    },
  },
};
</script>

<style scoped></style>
